/**
 * Custom hook para datos del capítulo
 * Centraliza la lógica de obtención de datos de capítulos
 */
import { useMemo } from "react";
import config from "../../core/config/config";
import type { Comic, Ficha, Language, UseChapterDataReturn } from "../types";

/**
 * Hook para obtener datos de un capítulo específico
 */
export const useChapterData = (
    comics: Comic[],
    chapterId: string,
    currentLanguage: Language
): UseChapterDataReturn => {
    const backendBaseUrl = config.backendUrl;

    // Encontrar el capítulo
    const chapter = useMemo((): Comic | undefined => {
        return comics.find((comic) => comic.id === chapterId);
    }, [comics, chapterId]);

    // Título del capítulo
    const chapterTitle = useMemo((): string => {
        if (!chapter) {
            return "Capítulo desconocido";
        }
        return chapter.title[currentLanguage] || "Sin título";
    }, [chapter, currentLanguage]);

    // Imágenes del cómic
    const comicImages = useMemo((): string[] => {
        if (!chapter) return [];
        return chapter.images[currentLanguage].map((img) => `${backendBaseUrl}/${img}`);
    }, [chapter, currentLanguage, backendBaseUrl]);

    // Fichas/artículos
    const fichas = useMemo((): Ficha[] | null => {
        return chapter?.fichas || null;
    }, [chapter]);

    // Extras
    const extraData = useMemo((): Array<{ image: string; subtitle: string }> => {
        if (!chapter?.extras) return [];
        return chapter.extras
            .flatMap((extra) => extra[currentLanguage])
            .map((img) => {
                if (typeof img === "string") {
                    return { image: backendBaseUrl + "/comics/" + img.replace(/^\//, ""), subtitle: "" };
                }
                // Si es objeto, asegurar que la imagen tenga el backendBaseUrl
                const imagePath = img.image.startsWith("http") ? img.image : backendBaseUrl + img.image;
                return { ...img, image: imagePath };
            });
    }, [chapter, currentLanguage, backendBaseUrl]);

    return {
        chapter,
        chapterTitle,
        comicImages,
        fichas,
        extraData,
        backendBaseUrl,
    };
};

export default useChapterData;
